import { fork, put, select, takeEvery } from "redux-saga/effects";

import {
    createHideLoadingIndicatorAction,
    createShowLoadingIndicatorAction,
} from "@multiweb/reactCore/bridge";
import {
    createDeselectAllRecordsAction,
    createInitDataSourceAction,
    createSelectRecordAction,
    createSelectRecordsAction,
    createSetDatasourceMultiselectModeAction,
    createSetLastSelectedRecordIdAction,
    createToggleSelectedRecordCheckAction,
    createUncheckAllRecordsAction,
    getMultiselectMode,
} from "@multiweb/reactCore/datasource";
import { createLoadLocalizationKeysAction } from "@multiweb/reactCore/i18n";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import { createTrySelectRecordAction } from "./actions";
import {
    FETCHING_KEY_FRAME_LOCALIZATIONS,
    FETCHING_KEY_STATIC_FRAMES_ITEMS_LOCALIZATION,
    HANDLE_RECORD_CLICK,
    INIT_FRAME,
    INIT_FRAME_WITH_DATASOURCE,
    SET_ALLOW_MULTISELECT,
    SET_MULTISELECT_MODE,
    SET_SELECTED_RECORDS,
    TRY_SELECT_RECORD,
} from "./constants";
import { staticLocKeys } from "./locKeys";
import { getAllowMultiselectMode } from "./selectors";
import {
    IHandleRecordClickAction,
    IInitFrameAction,
    IInitFrameWithDataSourceAction,
    ISetAllowMultiselectAction,
    ISetMultiselectModeAction,
    ISetSelectedRecordsAction,
    ITrySelectRecordAction,
} from "./types";

export const saga: SagaWithReturnType = function* framesSaga() {
    yield fork(loadFramesDataSaga);
    yield takeEvery(INIT_FRAME_WITH_DATASOURCE, initFrameWithDataSourceSaga);
    yield takeEvery([INIT_FRAME_WITH_DATASOURCE, INIT_FRAME], initFrameSaga);
    yield takeEvery(HANDLE_RECORD_CLICK, handleRecordClickSaga);
    yield takeEvery(SET_MULTISELECT_MODE, setMultiselectSaga);
    yield takeEvery(SET_SELECTED_RECORDS, setSelectedRecordsSaga);
    yield takeEvery(TRY_SELECT_RECORD, trySelectRecordSaga);
    yield takeEvery(SET_ALLOW_MULTISELECT, setAllowMultiSelectSaga);
};

/**
 * Sdružuje načítání potřebných dat pro framy a řeší zobrazení indikátoru načítání.
 */
export const loadFramesDataSaga: SagaWithReturnType = function* loadFramesDataSaga() {
    yield put(createShowLoadingIndicatorAction());
    yield fork(loadStaticFramesItemsLocalizationSaga);
    yield put(createHideLoadingIndicatorAction());
};

/** Obsluhuje načtení lokalizace pro statické položky framů. */
export const loadStaticFramesItemsLocalizationSaga: SagaWithReturnType = function* loadStaticFramesItemsLocalizationSaga() {
    yield put(createLoadLocalizationKeysAction(staticLocKeys, FETCHING_KEY_STATIC_FRAMES_ITEMS_LOCALIZATION));
};

/** Při inicializaci framu, který využívá DataSource, vyšle akci pro inicializaci datasource. */
export const initFrameWithDataSourceSaga: SagaWithReturnType<[IInitFrameWithDataSourceAction]> = function* initFrameWithDataSourceSaga(action) {
    const { dataSourceConfig, hasDetailForm, initialOrderBy, initialConditions } = action.payload;
    yield put(createInitDataSourceAction(action.meta.frameKey, dataSourceConfig, hasDetailForm, initialOrderBy, initialConditions));
};

/** Při inicializaci framu načte lokalizace, které jsou nastavené na daném frame. */
export const initFrameSaga: SagaWithReturnType<[IInitFrameAction]> = function* initFrameSaga(action) {
    const { noItemsLocKey, noItemsFilterLocKey, noItemsFulltextSearchLocKey } = action.payload.frameConfig.emptyMessageCustomization ?? {};
    const localizationKeys = [noItemsLocKey, noItemsFilterLocKey, noItemsFulltextSearchLocKey].filter(locKey => locKey) as string[];
    if (localizationKeys.length) {
        yield put(createLoadLocalizationKeysAction(localizationKeys, FETCHING_KEY_FRAME_LOCALIZATIONS));
    }
};

/**
 * Nastaví režim výběru více záznamů v datasource pokud je výběr povolen na frame - {@link IReactFramesState.allowMultiselect}.
 * V případě vypnutí tohoto režimu se záznamy v datasource odznačí.
 * Nastaví korektní multiselect mode conectoru v MultiWebu a aktualizuje react akce (mění se jejich viditelnost).
 */
export const setMultiselectSaga: SagaWithReturnType<[ISetMultiselectModeAction]> = function* setMultiselectSaga(
    action,
) {
    const { frameKey } = action.meta;
    const { multiselectMode } = action.payload;
    const allowMultiselect: SagaYieldReturn<typeof getAllowMultiselectMode> = yield select(getAllowMultiselectMode, frameKey);

    if (!allowMultiselect) {
        return;
    }

    yield put(createSetDatasourceMultiselectModeAction(frameKey, multiselectMode));
    if (!multiselectMode) {
        yield put(createDeselectAllRecordsAction(frameKey));
        yield put(createUncheckAllRecordsAction(frameKey));
    }
};

/**
 * Po zakázání režimu výběru více záznamů se zakáže i na datasource.
 */
export const setAllowMultiSelectSaga: SagaWithReturnType<[ISetAllowMultiselectAction]> = function* setAllowMultiSelectSaga(
    action,
) {
    const { allowMultiselect } = action.payload;
    const { frameKey } = action.meta;
    if (!allowMultiselect) {
        yield put(createSetDatasourceMultiselectModeAction(frameKey, false));
    }
};

/**
 * Při kliku na záznam zapne režim výběru více záznamů.
 * Při zrušení režimu výběru více záznamů na framu se tento režim zakáže i na datasource.
 */
export const handleRecordClickSaga: SagaWithReturnType<[IHandleRecordClickAction]> = function* handleRecordClickSaga(action) {
    const { frameKey } = action.meta;
    const { recordId } = action.payload;
    const allowMultiselect: SagaYieldReturn<typeof getAllowMultiselectMode> = yield select(getAllowMultiselectMode, frameKey);
    if (!allowMultiselect) {
        yield put(createTrySelectRecordAction(frameKey, recordId));
        return;
    }

    const actualMultiselectMode: ReturnType<typeof getMultiselectMode> = yield select(
        getMultiselectMode,
        frameKey,
    );
    if (!actualMultiselectMode) {
        yield put(createSetDatasourceMultiselectModeAction(frameKey, true));
    }
    yield put(createSetLastSelectedRecordIdAction(frameKey, recordId));
    yield put(createSelectRecordsAction(frameKey, [recordId]));
    yield put(createToggleSelectedRecordCheckAction(frameKey, recordId));
};

/**
 * Pokud je na framu povolen režim výběru více záznamů, vyšle akci pro výběr více záznamů na datasource.
 */
export const setSelectedRecordsSaga: SagaWithReturnType<[ISetSelectedRecordsAction]> = function* setSelectedRecordsSaga(
    action,
) {
    const { frameKey } = action.meta;
    const allowMultiselect: SagaYieldReturn<typeof getAllowMultiselectMode> = yield select(getAllowMultiselectMode, frameKey);
    if (allowMultiselect) {
        yield put(createSelectRecordsAction(frameKey, action.payload.selectedRecordsIds));
    }
};

/**
 * Pokud je na framu povolen režim výběru více záznamů, vyšle akci pro výběr záznamu na datasource.
 */
export const trySelectRecordSaga: SagaWithReturnType<[ITrySelectRecordAction]> = function* trySelectRecordSaga(
    action,
) {
    const { recordId } = action.payload;
    const { frameKey } = action.meta;
    yield put(createSetLastSelectedRecordIdAction(frameKey, recordId));
    const allowMultiselect: SagaYieldReturn<typeof getAllowMultiselectMode> = yield select(getAllowMultiselectMode, frameKey);
    if (allowMultiselect) {
        yield put(createSelectRecordAction(frameKey, recordId));
    }
};
